import React, { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AuditLogs from './pages/AuditLogs';

// Master Pages
import Organizations from './pages/masters/Organizations';
import Branches from './pages/masters/Branches';
import Stores from './pages/masters/Stores';
import Employees from './pages/masters/Employees';
import Roles from './pages/masters/Roles';
import Customers from './pages/masters/Customers';
import Vendors from './pages/masters/Vendors';
import UOMs from './pages/masters/UOMs';
import Items from './pages/masters/Items';
import Machines from './pages/masters/Machines';
import Parts from './pages/masters/Parts';
import Moulds from './pages/masters/Moulds';
import MouldParts from './pages/masters/MouldParts';
import BOM from './pages/masters/BOM';
import CycleTimes from './pages/masters/CycleTimes';
import RejectionReasons from './pages/masters/RejectionReasons';
import Shifts from './pages/masters/Shifts';

// Transaction Pages
import Purchases from './pages/transactions/Purchases';
import IncomingInspection from './pages/transactions/IncomingInspection';
import ItemRequests from './pages/transactions/ItemRequests';
import ItemRequestResponses from './pages/transactions/ItemRequestResponses';
import ProductionTemplate from './pages/transactions/ProductionTemplate';
import ProductionRun from './pages/transactions/ProductionRun';
import PartInspection from './pages/transactions/PartInspection';
import SalesOrders from './pages/transactions/SalesOrders';
import DispatchChallan from './pages/transactions/DispatchChallan';
import CustomerPurchaseOrders from './pages/transactions/CustomerPurchaseOrders';

// Report Pages
import Inventory from './pages/reports/Inventory';
import ProductionReport from './pages/reports/ProductionReport';
import ProcurementPlanning from './pages/reports/ProcurementPlanning';

// Components
import Sidebar from './components/Sidebar';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Layout with Sidebar
const AppLayout = () => {
  const location = useLocation();
  const mainRef = useRef(null);
  
  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo(0, 0);
    }
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <main ref={mainRef} className="flex-1 overflow-auto h-screen">
        <Outlet />
      </main>
    </div>
  );
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Route */}
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } 
          />
          
          {/* Protected Routes */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/audit-logs" element={<AuditLogs />} />
            
            {/* Master Routes */}
            <Route path="/masters/organizations" element={<Organizations />} />
            <Route path="/masters/branches" element={<Branches />} />
            <Route path="/masters/stores" element={<Stores />} />
            <Route path="/masters/employees" element={<Employees />} />
            <Route path="/masters/roles" element={<Roles />} />
            <Route path="/masters/customers" element={<Customers />} />
            <Route path="/masters/vendors" element={<Vendors />} />
            <Route path="/masters/uoms" element={<UOMs />} />
            <Route path="/masters/items" element={<Items />} />
            <Route path="/masters/machines" element={<Machines />} />
            <Route path="/masters/moulds" element={<Moulds />} />
            <Route path="/masters/mould-parts" element={<MouldParts />} />
            <Route path="/masters/parts" element={<Parts />} />
            <Route path="/masters/bom" element={<BOM />} />
            <Route path="/masters/cycle-times" element={<CycleTimes />} />
            <Route path="/masters/rejection-reasons" element={<RejectionReasons />} />
            <Route path="/masters/shifts" element={<Shifts />} />
            
            {/* Transaction Routes */}
            <Route path="/transactions/purchases" element={<Purchases />} />
            <Route path="/transactions/incoming-inspection" element={<IncomingInspection />} />
            <Route path="/transactions/item-requests" element={<ItemRequests />} />
            <Route path="/transactions/item-responses" element={<ItemRequestResponses />} />
            <Route path="/transactions/production-templates" element={<ProductionTemplate />} />
            <Route path="/transactions/production-runs" element={<ProductionRun />} />
            <Route path="/transactions/part-inspection" element={<PartInspection />} />
            <Route path="/transactions/customer-pos" element={<CustomerPurchaseOrders />} />
            <Route path="/transactions/sales-orders" element={<SalesOrders />} />
            <Route path="/transactions/dispatch-challans" element={<DispatchChallan />} />
            
            {/* Report Routes */}
            <Route path="/reports/inventory" element={<Inventory />} />
            <Route path="/reports/production" element={<ProductionReport />} />
            <Route path="/reports/procurement-planning" element={<ProcurementPlanning />} />
          </Route>
          
          {/* Redirect root to dashboard or login */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
